
import React, { useState, useEffect, useRef } from 'react';
import { NotificationManager } from 'react-notifications';
import { Quantum } from 'ldrs/react';
import 'ldrs/react/Quantum.css';
import '../assets/css/timeline.css';
import { handleWheel, handleMouseEnter, handleMouseLeave } from '../utils/Scroll';
import DashboardFooter from '../components/DashboardFooter';

const AppointmentTimeline = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [scrollPosition, setScrollPosition] = useState(0);
  const timelineRef = useRef(null);

  const token = localStorage.getItem("token");

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Handle window resize to recalculate timeline width
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to recalculate positions
      setCurrentTime(new Date());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate width when timeline ref becomes available
  useEffect(() => {
    if (timelineRef.current) {
      // Force re-render to recalculate width
      setCurrentTime(new Date());
    }
  }, [timelineData]);

  // Fetch timeline data
  const fetchTimelineData = async (date) => {
    if (!date) return;

    try {
      setLoading(true);

      const payload = {
        date: date,
        startTime: '00:00:00',
        endTime: '23:59:59'
      };

      const response = await fetch(`${process.env.REACT_APP_HOST_API_URL}/admin-user/employee-timeline/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch timeline data');
      }

      const data = await response.json();
      setTimelineData(data);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      NotificationManager.error('Failed to fetch timeline data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimelineData(selectedDate);
  }, [selectedDate]);

  // Get current time in HH:MM format
  const getCurrentTimeString = () => {
    return currentTime.toTimeString().slice(0, 5);
  };

  // Check if employee is currently working
  const isEmployeeWorking = (employee) => {
    const currentTimeStr = getCurrentTimeString();
    return employee.assigned_appointments?.some(appointment =>
      currentTimeStr >= appointment.start_time &&
      currentTimeStr <= appointment.end_time
    );
  };

  // Filter employees - only show those with appointments
  const getFilteredEmployees = () => {
    if (!timelineData || !timelineData.employees) return [];

    // First filter to only employees with appointments
    const employeesWithAppointments = timelineData.employees.filter(emp =>
      emp.assigned_appointments && emp.assigned_appointments.length > 0
    );

    switch (filter) {
      case 'available':
        return employeesWithAppointments.filter(emp => emp.is_available);
      case 'busy':
        return employeesWithAppointments.filter(emp => !emp.is_available);
      case 'working':
        return employeesWithAppointments.filter(emp => isEmployeeWorking(emp));
      default:
        return employeesWithAppointments;
    }
  };

  // Generate time slots for the day (3 AM to 12 AM)
  const generateTimeSlots = () => {
    const slots = [];
    // Hours 3 AM to 11 PM (3 to 23)
    for (let hour = 3; hour <= 23; hour++) {
      slots.push({
        time: `${hour.toString().padStart(2, '0')}:00`,
        hour: hour
      });
    }
    // Add midnight (12 AM)
    slots.push({
      time: '00:00',
      hour: 24
    });
    return slots;
  };

  // Calculate dynamic width per hour based on available space
  const getWidthPerHour = () => {
    if (!timelineRef.current) {
      console.log('Timeline ref not available, using fallback width');
      return 50; // fallback
    }

    const containerWidth = timelineRef.current.offsetWidth;
    const channelHeaderWidth = 200; // Fixed width for employee column
    const availableWidth = containerWidth - channelHeaderWidth;
    const totalHours = 21; // 3 AM to 12 AM (midnight) = 21 hours

    const calculatedWidth = availableWidth / totalHours;
    const finalWidth = Math.max(calculatedWidth, 25); // Minimum 25px per hour

    console.log('Width calculation:', {
      containerWidth,
      channelHeaderWidth,
      availableWidth,
      totalHours,
      calculatedWidth,
      finalWidth
    });

    return finalWidth;
  };

  // Calculate time position for current time
  const getCurrentTimePosition = () => {
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();

    console.log('Current time:', currentHour + ':' + currentMinute);

    // Handle time range from 3 AM to 12 AM (midnight)
    if (currentHour < 3 && currentHour > 0) {
      console.log('Current time outside range (3 AM - 12 AM)');
      return null;
    }

    const widthPerHour = getWidthPerHour();

    // Convert to minutes from 3 AM for easier calculation
    const minutesFrom3AM = (currentHour === 0 ? 24 : currentHour) * 60 + currentMinute - (3 * 60);

    // Convert to pixels
    const totalPosition = (minutesFrom3AM / 60) * widthPerHour;

    console.log('Width per hour:', widthPerHour);
    console.log('Minutes from 3 AM:', minutesFrom3AM);
    console.log('Total position:', totalPosition);

    return totalPosition;
  };

  // Generate schedule bars for employee
  const generateScheduleBars = (employee) => {
    const bars = [];
    const appointments = employee.assigned_appointments || [];
    const widthPerHour = getWidthPerHour();

    // Sort appointments by start time
    const sortedAppointments = appointments.sort((a, b) =>
      a.start_time.localeCompare(b.start_time)
    );

    sortedAppointments.forEach((appointment, index) => {
      const startTime = appointment.start_time;
      const endTime = appointment.end_time;

      // Parse time strings (format: "HH:MM")
      const [startHourStr, startMinuteStr] = startTime.split(':');
      const [endHourStr, endMinuteStr] = endTime.split(':');

      const startHour = parseInt(startHourStr);
      const startMinute = parseInt(startMinuteStr);
      const endHour = parseInt(endHourStr);
      const endMinute = parseInt(endMinuteStr);

      console.log(`Appointment ${index}:`, {
        startTime,
        endTime,
        startHour,
        startMinute,
        endHour,
        endMinute
      });

      // Convert to minutes from 3 AM for easier calculation
      const startMinutesFrom3AM = (startHour === 0 ? 24 : startHour) * 60 + startMinute - (3 * 60);
      const endMinutesFrom3AM = (endHour === 0 ? 24 : endHour) * 60 + endMinute - (3 * 60);

      // Convert to pixels
      const startPosition = (startMinutesFrom3AM / 60) * widthPerHour;
      const endPosition = (endMinutesFrom3AM / 60) * widthPerHour;
      const width = endPosition - startPosition;

      console.log(`Position calculation:`, {
        startMinutesFrom3AM,
        endMinutesFrom3AM,
        startPosition,
        endPosition,
        width,
        widthPerHour
      });

      // Only show if within our time range (3 AM - 12 AM)
      if ((startHour >= 3 && startHour <= 23) || startHour === 0) {
        bars.push({
          id: index,
          startTime: appointment.start_time,
          endTime: appointment.end_time,
          clientName: appointment.client_name,
          left: startPosition,
          width: Math.max(width, 20), // Minimum width of 20px
          isCurrent: isTimeInRange(getCurrentTimeString(), appointment.start_time, appointment.end_time)
        });

        console.log(`Added appointment bar:`, {
          left: startPosition,
          width: Math.max(width, 20),
          clientName: appointment.client_name
        });
      } else {
        console.log(`Appointment outside range:`, appointment.start_time);
      }
    });

    return bars;
  };

  // Check if current time is within appointment range
  const isTimeInRange = (currentTime, startTime, endTime) => {
    return currentTime >= startTime && currentTime <= endTime;
  };

  // Handle scroll to current time
  const scrollToCurrentTime = () => {
    const currentPosition = getCurrentTimePosition();
    if (currentPosition && timelineRef.current) {
      const scrollLeft = Math.max(0, currentPosition - 200); // Offset to center
      timelineRef.current.scrollLeft = scrollLeft;
    }
  };

  // Handle today button click
  const handleTodayClick = () => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
    // The useEffect will automatically fetch data when selectedDate changes
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const timeSlots = generateTimeSlots();
  const filteredEmployees = getFilteredEmployees();
  const currentTimePosition = getCurrentTimePosition();

  return (
      <div className="d-flex flex-column min-vh-100 pb-2 px-3">
    {/* Main content area (scrolls normally) */}
    <div className="flex-grow-1">
    <div className="container-fluid  timeline-wrapper px-2  px-md-4"
      style={{ width: '100vw', maxWidth: '100%', overflow: 'hidden', paddingTop: '20px', maxHeight:'100%' }}
    >
      <div>
 <div className="row mb-4 bg-white shadow-sm border-bottom sticky-top py-3 px-md-3 px-2 rounded"
        style={{

          background: "linear-gradient(135deg, #0564bf, #89CFF0"
        }}>
        {/* Header Controls */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">

          <div className="">
            <h4 className="text-light mb-1 ">
              Results for {formatDateForDisplay(selectedDate)}
            </h4>
            <p className="text-light mb-0 ">
              {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} with appointments
            </p>
          </div>
          <div className="flex gap-2">

            <input
              id="datePicker"
              type="date"
              className="form-control date-picker-custom form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                backgroundColor: '#fff',
                width: '150px'
              }}
            />
            <button
              className="btn btn-outline-light"
              onClick={handleTodayClick}
            >
              <i className="fas fa-calendar-day me-2"></i>
              Today
            </button>
          </div>
        </div>

      </div>
     

        {/* TV Guide Timeline */}
        <div className="  ">
          <div className=" w-100 border-0 py-2">
            <div className="card-body p-0" style={{ padding: 0, width: '100%', maxWidth: '100%' }}
              onWheel={handleWheel}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {loading ? (
                <div className="text-center py-5">
                  <Quantum size="50" speed="1.75" color="#0564bf" />
                  <p className="mt-3 text-muted">Loading TV guide...</p>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-tv fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No employees with appointments found for the selected criteria</p>
                </div>
              ) : (
                <div className="tv-guide-container">
                  {/* Time Header */}
                  <div className="tv-guide-header">
                    <div className="channel-header">Employee</div>
                    <div className="time-header">
                      {timeSlots.map((slot, index) => (
                        <div key={index} className="time-marker">
                          <div className="time-label"></div>
                          {/* <div className="time-line"></div> */}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Current Time Indicator */}
                  {currentTimePosition !== null && (
                    <div
                      className="current-time-indicator"
                      style={{ left: `${200 + currentTimePosition}px` }}
                    >
                      <div className="current-time-line"></div>
                      <div className="current-time-label">
                        {getCurrentTimeString()}
                      </div>
                    </div>
                  )}
                  {/* Comprehensive Debug Panel */}
                  {/* <div style={{ 
                    position: 'absolute', 
                    top: '10px', 
                    right: '10px', 
                    background: 'rgba(0,0,0,0.9)', 
                    color: 'white', 
                    padding: '10px',
                    fontSize: '11px',
                    zIndex: 1000,
                    maxWidth: '300px',
                    borderRadius: '5px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Timeline Debug Info</div>
                    <div>Current Time: {getCurrentTimeString()}</div>
                    <div>Current Position: {currentTimePosition !== null ? currentTimePosition.toFixed(1) + 'px' : 'N/A'}</div>
                    <div>Width per hour: {getWidthPerHour().toFixed(1)}px</div>
                    <div>Container width: {timelineRef.current ? timelineRef.current.offsetWidth + 'px' : 'N/A'}</div>
                    <div>Available width: {timelineRef.current ? (timelineRef.current.offsetWidth - 200) + 'px' : 'N/A'}</div>
                    <div>Total hours: 21 (3 AM - 12 AM)</div>
                    <div>Time slots: {timeSlots.length}</div>
                    <div>Employees: {filteredEmployees.length}</div>
                    <div style={{ marginTop: '5px', fontSize: '10px', color: '#ccc' }}>
                      Timeline should fit in screen width without horizontal scrolling
                    </div>
                  </div> */}

                  {/* Timeline width indicator */}
                  {/* <div style={{
                    position: 'absolute',
                    top: '30px',
                    left: '200px',
                    height: '5px',
                    width: `${21 * getWidthPerHour()}px`,
                    background: 'linear-gradient(90deg, #ff0000, #00ff00)',
                    zIndex: 998,
                    pointerEvents: 'none',
                    borderRadius: '2px'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-15px',
                      left: '0',
                      fontSize: '10px',
                      color: 'red',
                      fontWeight: 'bold'
                    }}>
                      Timeline Width: {21 * getWidthPerHour()}px
                    </div>
                  </div> */}

                  {/* Time markers debug overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '40px',
                    left: '200px',
                    right: '0',
                    height: '20px',
                    // background: 'rgba(255,0,0,0.1)',
                    zIndex: 999,
                    pointerEvents: 'none'
                  }}>
                    {timeSlots.map((slot, index) => {
                      // Convert to minutes from 3 AM
                      const minutesFrom3AM = (slot.hour === 24 ? 24 : slot.hour) * 60 - (3 * 60);
                      const position = (minutesFrom3AM / 60) * getWidthPerHour();
                      return (
                        <div key={index} style={{
                          position: 'absolute',
                          left: `${position}px`,
                          top: '0',
                          height: '20px',
                          width: '1px',
                          background: 'grey',
                          fontSize: '10px',
                          color: 'black'
                        }}>
                          <strong className="text-muted">{slot.time}</strong>
                          {/* {slot.time} */}
                        </div>
                      );
                    })}
                  </div>

                  {/* Employee Channels */}
                  <div className="tv-guide-channels" ref={timelineRef}>
                    {filteredEmployees.map((employee, index) => {
                      const scheduleBars = generateScheduleBars(employee);
                      const isWorking = isEmployeeWorking(employee);

                      return (
                        <div key={employee.employee_id} className="tv-channel">
                          <div className="channel-info">
                            <div className="channel-avatar">
                              {employee.employee_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="channel-details">
                              <div className="channel-name ">{employee.employee_name}</div>
                              <div className="channel-username">@{employee.employee_username}</div>
                              <div className={`channel-status ${employee.is_available ? 'available' : 'busy'} ${isWorking ? 'working' : ''}`}>
                                {isWorking ? '🔴 LIVE' : employee.is_available ? '🟢 Available' : '🟡 Busy'}
                              </div>
                            </div>
                          </div>

                          <div className="channel-schedule">
                            {scheduleBars.map((bar) => (
                              <div
                                key={bar.id}
                                className={`schedule-bar ${bar.isCurrent ? 'current' : ''}`}
                                style={{
                                  left: `${bar.left}px`,
                                  width: `${bar.width}px`
                                }}
                                title={`${bar.clientName} (${bar.startTime} - ${bar.endTime})`}
                              >
                                <div className="schedule-content">
                                  <div className="schedule-title">{bar.clientName}</div>
                                  <div className="schedule-time">
                                    {bar.startTime} - {bar.endTime}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
      </div>
      </div>
      <DashboardFooter/>
    </div>
  );
};

export default AppointmentTimeline;
